import { useState, useEffect, useMemo, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useTheme } from '@/contexts/ThemeContext'
import {
  MODAL_PAPER_STYLES,
  MODAL_TITLE_STYLES,
  MODAL_CONTENT_STYLES,
  MODAL_BUTTON_STYLES,
} from '@/components/shared/modalStyles'
import { copyToClipboard } from '@/utils/clipboard'
import { useNotification } from '@/hooks/useNotification'
import { useChainId } from 'wagmi'
import useSafe from '@/localsafe.eth/hooks/useSafe'
import { ethers } from 'ethers'
import { getEnv } from '@/config/env'
import { getContractName as getContractNameUtil } from './utils'
import type { TransactionHistoryItem } from './types'

interface TransactionDetailsModalProps {
  open: boolean
  onClose: () => void
  transaction: TransactionHistoryItem | null
  chain: import('viem').Chain | undefined
  decimals: number
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transaction-tabpanel-${index}`}
      aria-labelledby={`transaction-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

export default function TransactionDetailsModal({
  open,
  onClose,
  transaction,
}: TransactionDetailsModalProps) {
  const { colors } = useTheme()
  const { showSuccess, showError } = useNotification()
  const chainId = useChainId()
  const [activeTab, setActiveTab] = useState(0)
  const [domainHash, setDomainHash] = useState<string | null>(null)
  const [messageHash, setMessageHash] = useState<string | null>(null)
  const [safeTxHash, setSafeTxHash] = useState<string | null>(null)
  const hiddenTextareaRef = useRef<HTMLTextAreaElement>(null)

  const handleCopyAddress = async (
    address: string,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    try {
      // Try using hidden textarea in dialog first (better for mobile)
      if (hiddenTextareaRef.current) {
        hiddenTextareaRef.current.value = address
        hiddenTextareaRef.current.focus()
        hiddenTextareaRef.current.select()
        hiddenTextareaRef.current.setSelectionRange(0, address.length)

        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(address)
            showSuccess('Address copied!')
            return
          }
        } catch (clipboardError) {
          console.warn(
            'Clipboard API failed, trying execCommand:',
            clipboardError
          )
        }

        try {
          interface DocumentWithExecCommand extends Document {
            execCommand(
              commandId: string,
              showUI?: boolean,
              value?: string
            ): boolean
          }
          const successful = (document as DocumentWithExecCommand).execCommand(
            'copy'
          )
          if (successful) {
            showSuccess('Address copied!')
            return
          }
        } catch (execError) {
          console.warn('execCommand failed:', execError)
        }
      }

      // Fallback to utility function
      await copyToClipboard(address)
      showSuccess('Address copied!')
    } catch (error) {
      console.error('Failed to copy address:', error)
      showError('Failed to copy address')
    }
  }

  const transactionJson = useMemo(() => {
    if (!transaction?.tx) return null
    const tx = transaction.tx
    return {
      to: tx.data.to || '0x0000000000000000000000000000000000000000',
      data: tx.data.data || '0x',
      value: tx.data.value?.toString() || '0',
      operation: tx.data.operation || 0,
      baseGas: tx.data.baseGas?.toString() || '0',
      gasPrice: tx.data.gasPrice?.toString() || '0',
      gasToken:
        tx.data.gasToken || '0x0000000000000000000000000000000000000000',
      nonce: tx.data.nonce || 0,
      refundReceiver:
        tx.data.refundReceiver || '0x0000000000000000000000000000000000000000',
      safeTxGas: tx.data.safeTxGas?.toString() || '0',
    }
  }, [transaction])

  const { getSafeTransactionHash } = useSafe(
    transaction?.safeAddress as `0x${string}`
  )

  useEffect(() => {
    if (activeTab !== 1) return
    if (!transaction || !chainId || !transaction.safeAddress) return

    const calculateHashes = async () => {
      try {
        const safeTx = transaction.tx
        const safeAddress = transaction.safeAddress

        const txHash = await getSafeTransactionHash(safeTx)
        setSafeTxHash(txHash)

        const domain = {
          chainId: chainId,
          verifyingContract: safeAddress,
        }

        const types = {
          SafeTx: [
            { name: 'to', type: 'address' },
            { name: 'value', type: 'uint256' },
            { name: 'data', type: 'bytes' },
            { name: 'operation', type: 'uint8' },
            { name: 'safeTxGas', type: 'uint256' },
            { name: 'baseGas', type: 'uint256' },
            { name: 'gasPrice', type: 'uint256' },
            { name: 'gasToken', type: 'address' },
            { name: 'refundReceiver', type: 'address' },
            { name: 'nonce', type: 'uint256' },
          ],
        }

        const message = {
          to: safeTx.data.to || '0x0000000000000000000000000000000000000000',
          value: safeTx.data.value || '0',
          data: safeTx.data.data || '0x',
          operation: safeTx.data.operation || 0,
          safeTxGas: safeTx.data.safeTxGas || '0',
          baseGas: safeTx.data.baseGas || '0',
          gasPrice: safeTx.data.gasPrice || '0',
          gasToken:
            safeTx.data.gasToken ||
            '0x0000000000000000000000000000000000000000',
          refundReceiver:
            safeTx.data.refundReceiver ||
            '0x0000000000000000000000000000000000000000',
          nonce: safeTx.data.nonce || 0,
        }

        const domainHashValue = ethers.TypedDataEncoder.hashDomain(domain)
        const messageHashValue = ethers.TypedDataEncoder.hashStruct(
          'SafeTx',
          types,
          message
        )

        setDomainHash(domainHashValue)
        setMessageHash(messageHashValue)
      } catch (error) {
        console.error('Failed to calculate hashes:', error)
        setDomainHash(null)
        setMessageHash(null)
        setSafeTxHash(null)
      }
    }

    calculateHashes()
  }, [activeTab, transaction, chainId, getSafeTransactionHash])

  if (!transaction) return null

  const dividerStyles = {
    borderColor: colors.primary,
    opacity: 0.3,
  }

  const detailRowContainerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    py: 1.5,
    gap: 2,
  }

  const detailRowLabelStyles = {
    color: colors.yellow,
    fontFamily: "'TWKEverett', sans-serif",
    fontSize: '0.95rem',
    fontWeight: 600,
    minWidth: '120px',
  }

  const detailRowValueContainerStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flex: 1,
    justifyContent: 'flex-end',
  }

  const detailRowValueStyles = {
    color: colors.text,
    fontFamily: "'TWKEverett', sans-serif",
    fontSize: '0.95rem',
    wordBreak: 'break-word',
    textAlign: 'right',
  }

  const detailRowCopyButtonStyles = {
    color: colors.primary,
    padding: '4px',
    '&:hover': {
      backgroundColor: `${colors.primary}20`,
    },
  }

  const { tx } = transaction
  const { stngProxy, treasurySafe, operationalSafeFactory } = getEnv()

  const getContractName = (address: string | undefined): string => {
    if (!address) return ''
    return getContractNameUtil(
      address,
      stngProxy,
      treasurySafe,
      operationalSafeFactory
    )
  }

  const DetailRow = ({
    label,
    value,
    copyable = false,
  }: {
    label: string
    value: string | null | undefined
    copyable?: boolean
  }) => {
    if (!value) return null
    return (
      <Box sx={detailRowContainerStyles}>
        <Typography sx={detailRowLabelStyles}>{label}:</Typography>
        <Box sx={detailRowValueContainerStyles}>
          <Typography sx={detailRowValueStyles}>{value}</Typography>
          {copyable && (
            <IconButton
              size="small"
              onClick={(e) => handleCopyAddress(value, e)}
              sx={detailRowCopyButtonStyles}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    )
  }

  const handleCopyJson = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (!transactionJson) return

    const jsonString = JSON.stringify(transactionJson, null, 2)

    try {
      // Try using hidden textarea in dialog first (better for mobile)
      if (hiddenTextareaRef.current) {
        hiddenTextareaRef.current.value = jsonString
        hiddenTextareaRef.current.focus()
        hiddenTextareaRef.current.select()
        hiddenTextareaRef.current.setSelectionRange(0, jsonString.length)

        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(jsonString)
            showSuccess('JSON copied to clipboard!')
            return
          }
        } catch (clipboardError) {
          console.warn(
            'Clipboard API failed, trying execCommand:',
            clipboardError
          )
        }

        try {
          interface DocumentWithExecCommand extends Document {
            execCommand(
              commandId: string,
              showUI?: boolean,
              value?: string
            ): boolean
          }
          const successful = (document as DocumentWithExecCommand).execCommand(
            'copy'
          )
          if (successful) {
            showSuccess('JSON copied to clipboard!')
            return
          }
        } catch (execError) {
          console.warn('execCommand failed:', execError)
        }
      }

      await copyToClipboard(jsonString)
      showSuccess('JSON copied to clipboard!')
    } catch (error) {
      console.error('Failed to copy JSON:', error)
      showError('Failed to copy JSON')
    }
  }

  const getSignatures = () => {
    if (!tx.signatures) return []
    return Array.from(tx.signatures.values())
  }

  const signatures = getSignatures()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          ...MODAL_PAPER_STYLES,
          backgroundColor: colors.background,
          border: `2px solid ${colors.primary}`,
          boxShadow: `0 0 20px ${colors.primaryShadow}`,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          ...MODAL_TITLE_STYLES,
          color: colors.primary,
          textShadow: `0 0 10px ${colors.primaryShadow}`,
        }}
      >
        Transaction Details
      </DialogTitle>
      <DialogContent>
        {/* Hidden textarea for mobile clipboard support */}
        <textarea
          ref={hiddenTextareaRef}
          style={{
            position: 'absolute',
            left: '-9999px',
            top: '0',
            width: '1px',
            height: '1px',
            opacity: '0',
            pointerEvents: 'none',
            zIndex: -1,
          }}
          readOnly
          aria-hidden="true"
        />
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                color: colors.text,
                '&.Mui-selected': {
                  color: colors.primary,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: colors.primary,
              },
            }}
          >
            <Tab label="Data" />
            <Tab label="Hashes" />
            <Tab label="JSON" />
          </Tabs>
        </Box>

        <Box sx={{ ...MODAL_CONTENT_STYLES, color: colors.text }}>
          {/* Data Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box sx={detailRowContainerStyles}>
                <Typography sx={detailRowLabelStyles}>To:</Typography>
                <Box sx={detailRowValueContainerStyles}>
                  <Typography sx={detailRowValueStyles}>
                    {tx.data.to
                      ? `${getContractName(tx.data.to)} ${tx.data.to}`
                      : 'dKZT'}
                  </Typography>
                  {tx.data.to && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleCopyAddress(tx.data.to || '', e)}
                      sx={detailRowCopyButtonStyles}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
              <Divider sx={dividerStyles} />

              <DetailRow
                label="Value"
                value={tx.data.value?.toString() || '0'}
              />
              <Divider sx={dividerStyles} />

              <DetailRow label="Data" value={tx.data.data || '0x'} />
              <Divider sx={dividerStyles} />

              <DetailRow
                label="Operation"
                value={`${tx.data.operation || 0} (call)`}
              />
              <Divider sx={dividerStyles} />

              <DetailRow
                label="SafeTxGas"
                value={tx.data.safeTxGas?.toString() || '0'}
              />
              <Divider sx={dividerStyles} />

              <DetailRow
                label="BaseGas"
                value={tx.data.baseGas?.toString() || '0'}
              />
              <Divider sx={dividerStyles} />

              <DetailRow
                label="GasPrice"
                value={tx.data.gasPrice?.toString() || '0'}
              />
              <Divider sx={dividerStyles} />

              <Box sx={detailRowContainerStyles}>
                <Typography sx={detailRowLabelStyles}>GasToken:</Typography>
                <Box sx={detailRowValueContainerStyles}>
                  <Typography sx={detailRowValueStyles}>
                    {tx.data.gasToken ===
                    '0x0000000000000000000000000000000000000000'
                      ? '0x0000...0000'
                      : tx.data.gasToken || '0x0000...0000'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) =>
                      handleCopyAddress(
                        tx.data.gasToken ||
                          '0x0000000000000000000000000000000000000000',
                        e
                      )
                    }
                    sx={detailRowCopyButtonStyles}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Divider sx={dividerStyles} />

              <Box sx={detailRowContainerStyles}>
                <Typography sx={detailRowLabelStyles}>
                  RefundReceiver:
                </Typography>
                <Box sx={detailRowValueContainerStyles}>
                  <Typography sx={detailRowValueStyles}>
                    {tx.data.refundReceiver ===
                    '0x0000000000000000000000000000000000000000'
                      ? '0x0000...0000'
                      : tx.data.refundReceiver || '0x0000...0000'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) =>
                      handleCopyAddress(
                        tx.data.refundReceiver ||
                          '0x0000000000000000000000000000000000000000',
                        e
                      )
                    }
                    sx={detailRowCopyButtonStyles}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Divider sx={dividerStyles} />

              <DetailRow label="Nonce" value={tx.data.nonce?.toString()} />
              <Divider sx={dividerStyles} />

              {signatures.map((sig, index) => (
                <Box key={index}>
                  <DetailRow
                    label={`Signature ${index + 1}`}
                    value={sig.data}
                    copyable
                  />
                  <Divider sx={dividerStyles} />
                </Box>
              ))}
            </Box>
          </TabPanel>

          {/* Hashes Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {domainHash ? (
                <>
                  <DetailRow label="Domain hash" value={domainHash} copyable />
                  <Divider sx={dividerStyles} />
                </>
              ) : (
                <>
                  <DetailRow label="Domain hash" value="Calculating..." />
                  <Divider sx={dividerStyles} />
                </>
              )}

              {messageHash ? (
                <>
                  <DetailRow
                    label="Message hash"
                    value={messageHash}
                    copyable
                  />
                  <Divider sx={dividerStyles} />
                </>
              ) : (
                <>
                  <DetailRow label="Message hash" value="Calculating..." />
                  <Divider sx={dividerStyles} />
                </>
              )}

              {safeTxHash ? (
                <>
                  <DetailRow label="safeTxHash" value={safeTxHash} copyable />
                  <Divider sx={dividerStyles} />
                </>
              ) : (
                <>
                  <DetailRow label="safeTxHash" value="Calculating..." />
                  <Divider sx={dividerStyles} />
                </>
              )}
            </Box>
          </TabPanel>

          {/* JSON Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: colors.overlay,
                  borderRadius: 1,
                  p: 2,
                  maxHeight: '400px',
                  overflow: 'auto',
                }}
              >
                <Typography
                  sx={{
                    color: colors.text,
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {transactionJson
                    ? JSON.stringify(transactionJson, null, 2)
                    : 'No transaction data'}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={(e) => handleCopyJson(e)}
                startIcon={<ContentCopyIcon />}
                sx={{
                  borderColor: colors.primary,
                  color: colors.primary,
                  '&:hover': {
                    borderColor: colors.primary,
                    backgroundColor: `${colors.primary}20`,
                  },
                }}
              >
                Copy JSON
              </Button>
            </Box>
          </TabPanel>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            ...MODAL_BUTTON_STYLES,
            backgroundColor: colors.primary,
            color: colors.background,
            '&:hover': {
              backgroundColor: colors.primary,
              opacity: 0.9,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
